// hooks/useClicks.js
// PrizeReal — Tracking de clicks en tiempo real (Firestore)
//
// Responsabilidades:
//   1. Generar el link único del usuario para un comercio
//   2. Leer el conteo real de clicks en tiempo real (onSnapshot)
//   3. Snapshotear la oferta al momento de compartir (CAPA 7)
//   4. Compartir por WhatsApp, Email u otras opciones

import { useState, useEffect, useCallback } from "react";
import {
  db,
  setDocument,
  getDocument,
  onSnapshot,
  serverTimestamp,
  doc,
} from "../config/firebase";
import { OFFER_SNAPSHOT } from "../config/security";
import useTranslation from "../i18n/useTranslation";

// ─── Generar o recuperar link único ──────────────────────────────────────────
/**
 * Genera o recupera el link único de un usuario para un comercio.
 * linkId = {uid}_{businessId} (reproducible, no aleatorio).
 * Snapshotea la oferta actual del comercio al momento de crear el link.
 *
 * @param {string} uid
 * @param {object} business  Objeto comercio del Sheet
 * @returns {Promise<{ linkId: string, url: string }>}
 */
const ensureLink = async (uid, business) => {
  const linkId   = `${uid}_${business.id}`;
  const existing = await getDocument("links", linkId);

  if (existing) {
    return { linkId, url: `https://prizereal.com/r/${linkId}` };
  }

  // Snapshot de la oferta al momento de compartir (CAPA 7)
  const offerSnapshot = {};
  OFFER_SNAPSHOT.FIELDS_TO_SNAPSHOT.forEach((field) => {
    offerSnapshot[field] = business[field] ?? null;
  });

  await setDocument("links", linkId, {
    linkId,
    uid,
    businessId:  business.id,
    clickCount:  0,
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
    [OFFER_SNAPSHOT.SNAPSHOT_KEY]: offerSnapshot,
  });

  return { linkId, url: `https://prizereal.com/r/${linkId}` };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useClicks = (uid, business) => {
  const { t } = useTranslation();

  const [clickCount, setClickCount]   = useState(0);
  const [linkId, setLinkId]           = useState(null);
  const [shareUrl, setShareUrl]       = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  const [error, setError]             = useState(null);

  // ─── Inicializar link ────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid || !business?.id) return;
    let cancelled = false;

    const init = async () => {
      setLoadingLink(true);
      try {
        const { linkId: id, url } = await ensureLink(uid, business);
        if (!cancelled) {
          setLinkId(id);
          setShareUrl(url);
        }
      } catch (err) {
        if (!cancelled) setError("link_init_failed");
        console.error("[useClicks] ensureLink error:", err);
      } finally {
        if (!cancelled) setLoadingLink(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [uid, business?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Suscripción en tiempo real al conteo de clicks ─────────────────────
  useEffect(() => {
    if (!linkId) return;

    const unsub = onSnapshot(doc(db, "links", linkId), (snap) => {
      if (snap.exists()) {
        setClickCount(snap.data().clickCount ?? 0);
      }
    });

    return unsub;
  }, [linkId]);

  // ─── Mensajes pre-armados ────────────────────────────────────────────────
  const buildMessages = useCallback(() => {
    if (!shareUrl || !business) return {};
    const url      = shareUrl;
    const whatsapp = t("share.whatsappMessage", { url });
    const subject  = t("share.emailSubject");
    const body     = t("share.emailBody", { url });
    return { url, whatsapp, subject, body };
  }, [shareUrl, business, t]);

  // ─── Acciones de compartir ───────────────────────────────────────────────

  /** Copia el link al portapapeles */
  const copyLink = useCallback(async () => {
    if (!shareUrl) return false;
    try {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } catch {
      // Fallback para browsers sin Clipboard API
      const el = document.createElement("textarea");
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    }
  }, [shareUrl]);

  /** Abre WhatsApp con el mensaje pre-armado */
  const shareWhatsApp = useCallback(() => {
    const { whatsapp } = buildMessages();
    if (!whatsapp) return;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(whatsapp)}`,
      "_blank",
      "noopener"
    );
  }, [buildMessages]);

  /** Abre cliente de email con subject y body pre-armados */
  const shareEmail = useCallback(() => {
    const { subject, body } = buildMessages();
    if (!subject) return;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [buildMessages]);

  /** Web Share API con fallback a copyLink */
  const shareNative = useCallback(async () => {
    const { url, whatsapp } = buildMessages();
    if (!url) return false;

    if (navigator.share) {
      try {
        await navigator.share({ title: business?.name, text: whatsapp, url });
        return true;
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[useClicks] shareNative error:", err);
        }
        return false;
      }
    }

    return copyLink();
  }, [buildMessages, business?.name, copyLink]);

  // ─── Oferta snapshotada ──────────────────────────────────────────────────
  /** Recupera la oferta que se guardó al momento de compartir */
  const getSnapshotOffer = useCallback(async () => {
    if (!linkId) return null;
    const link = await getDocument("links", linkId);
    return link?.[OFFER_SNAPSHOT.SNAPSHOT_KEY] ?? null;
  }, [linkId]);

  // ─── Progreso ────────────────────────────────────────────────────────────
  const clicksNeeded = business?.prize_clicks_needed ?? 1;
  const clicksToGo   = Math.max(0, clicksNeeded - clickCount);
  const canClaim     = clickCount >= clicksNeeded;
  const progressPct  = Math.min(100, Math.round((clickCount / clicksNeeded) * 100));

  return {
    linkId,
    shareUrl,
    clickCount,
    clicksNeeded,
    clicksToGo,
    canClaim,
    progressPct,
    loadingLink,
    error,
    copyLink,
    shareWhatsApp,
    shareEmail,
    shareNative,
    getSnapshotOffer,
  };
};

export default useClicks;
